import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface ImageUploadRequest {
  tagName: string;
  currentImageSrc: string;
  elementPosition: { x: number; y: number; width: number; height: number };
}

export interface ImageUploadResponse {
  success: boolean;
  newImageSrc?: string;
  error?: string;
}

@Component({
  selector: 'app-inline-image-uploader',
  imports: [CommonModule, FormsModule],
  templateUrl: './inline-image-uploader.component.html',
  styleUrls: ['./inline-image-uploader.component.scss']
})
export class InlineImageUploaderComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() uploadRequest: ImageUploadRequest | null = null;
  @Output() imageUpdated = new EventEmitter<{ tagName: string; newImageSrc: string; imageBlob: Blob }>();
  @Output() uploadCancelled = new EventEmitter<void>();

  @ViewChild('modalContainer') modalContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('browseBtn') browseBtn!: ElementRef<HTMLButtonElement>;

  // Image state
  originalImageSrc: string = '';
  newImageSrc: string = '';
  selectedFile: File | null = null;
  isUploading: boolean = false;
  imageDimensions: { width: number; height: number } | null = null;
  currentImageDimensions: { width: number; height: number } | null = null;

  // Minimized state (for future use)
  isMinimized: boolean = false;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  // Input properties for current data
  @Input() currentSupplierData: any = {};
  @Input() currentSemanticContent: { [tagName: string]: string } = {};

  ngOnInit(): void {
    // Listen for upload requests
    if (this.uploadRequest) {
      this.showUploader(this.uploadRequest);
    }
  }

  ngOnChanges(): void {
    console.log('InlineImageUploader: ngOnChanges called - uploadRequest:', this.uploadRequest);
    
    // Handle changes to uploadRequest input - always process new requests
    if (this.uploadRequest) {
      console.log('InlineImageUploader: Upload request received, showing uploader');
      this.showUploader(this.uploadRequest);
    }
  }

  ngAfterViewInit(): void {
    // Focus on browse button when modal opens
    if (this.browseBtn) {
      setTimeout(() => {
        this.browseBtn.nativeElement.focus();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // Cleanup
  }

  showUploader(request: ImageUploadRequest): void {
    console.log('InlineImageUploader: Showing uploader with request:', request);
    
    if (!request) {
      console.error('InlineImageUploader: No request provided to showUploader');
      return;
    }
    
    this.uploadRequest = request;
    this.originalImageSrc = request.currentImageSrc || '';
    this.newImageSrc = request.currentImageSrc || ''; // Start with current image
    this.selectedFile = null;
    this.isUploading = false;
    this.imageDimensions = null;
    this.currentImageDimensions = null;

    console.log('InlineImageUploader: Uploader state set - tag:', request.tagName, 'image:', this.newImageSrc);

    // Get current image dimensions if image exists
    if (this.originalImageSrc) {
      this.getImageDimensions(this.originalImageSrc).then(dimensions => {
        console.log('InlineImageUploader: Current image dimensions calculated:', dimensions);
        this.currentImageDimensions = dimensions;
        this.cdr.detectChanges();
      }).catch(error => {
        console.error('InlineImageUploader: Error getting current image dimensions:', error);
        this.currentImageDimensions = null;
        this.cdr.detectChanges();
      });
    }

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();

    // Focus on browse button after modal is positioned
    setTimeout(() => {
      if (this.browseBtn) {
        this.browseBtn.nativeElement.focus();
        console.log('InlineImageUploader: Focused on browse button');
      }
    }, 100);
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      console.log('InlineImageUploader: File selected:', file.name, file.type, file.size);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        console.error('InlineImageUploader: Invalid file type:', file.type);
        alert('Please select a valid image file.');
        return;
      }

      this.selectedFile = file;
      this.isUploading = false;

      // Create preview and directly update the image
      const reader = new FileReader();
      reader.onload = (e) => {
        this.newImageSrc = e.target?.result as string;
        console.log('InlineImageUploader: Image preview created and updated');
        
        // Get image dimensions
        this.getImageDimensions(this.newImageSrc).then(dimensions => {
          console.log('InlineImageUploader: Image dimensions calculated:', dimensions);
          this.imageDimensions = dimensions;
          this.cdr.detectChanges();
        }).catch(error => {
          console.error('InlineImageUploader: Error getting image dimensions:', error);
          this.imageDimensions = null;
          this.cdr.detectChanges();
        });
      };
      reader.readAsDataURL(file);
    }
  }

  cancelUpload(): void {
    this.uploadCancelled.emit();
    this.closeModal();
  }

  // This method is called on form submit (Update button)
  onUpdate(): void {
    if (this.uploadRequest && this.selectedFile) {
      // Emit the event with the new image data and blob
      this.imageUpdated.emit({
        tagName: this.uploadRequest.tagName,
        newImageSrc: this.newImageSrc.trim(),
        imageBlob: this.selectedFile
      });
      this.closeModal();
    } else if (this.uploadRequest && this.newImageSrc.trim()) {
      // Handle case where image was already loaded but no new file selected
      this.imageUpdated.emit({
        tagName: this.uploadRequest.tagName,
        newImageSrc: this.newImageSrc.trim(),
        imageBlob: null as any // Will be handled by the parent component
      });
      this.closeModal();
    }
  }

  private closeModal(): void {
    this.uploadRequest = null;
    this.originalImageSrc = '';
    this.newImageSrc = '';
    this.selectedFile = null;
    this.isUploading = false;
    this.imageDimensions = null;
    this.currentImageDimensions = null;
    // Remove highlight from preview
    if (window && window.parent) {
      window.parent.postMessage({ type: 'closeInlineEditor' }, '*');
    } else if (window) {
      window.postMessage({ type: 'closeInlineEditor' }, '*');
    }
  }

  // Helper methods to get current data
  private getCurrentSupplierData(): any {
    return this.currentSupplierData;
  }

  private getCurrentSemanticContent(): { [tagName: string]: string } {
    return this.currentSemanticContent;
  }

  // Handle modal clicks (no backdrop click to close)
  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private getImageDimensions(imageSrc: string): Promise<{ width: number; height: number }> {
    console.log('InlineImageUploader: Getting dimensions for image:', imageSrc.substring(0, 50) + '...');
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        console.log('InlineImageUploader: Image loaded successfully, natural dimensions:', img.naturalWidth, 'x', img.naturalHeight);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => {
        console.error('InlineImageUploader: Failed to load image for dimensions');
        reject(new Error('Failed to load image'));
      };
      img.src = imageSrc;
    });
  }
} 